import { alpha, Theme } from '@mui/material';
import themeV2 from '../dap';
import { TabsClass, ToggleButtonGroupClass } from '../dap/classes';
import {
  blockTab,
  dataGridBoldClass,
  dataGridCellHoverClass,
  dataGridCellNoFocusOutlineClass,
  dataGridColorRowClass,
  dataGridHorizontalPaddingXSClass,
  dataGridNoHorizontalPaddingClass,
  dataGridRowHoverClass,
  dataGridSelectedRowClass,
  dataGridWhiteRowClass,
  dataGridWithCellBordersClass,
} from './styleConstants';
import { ExpandMoreRounded } from '@mui/icons-material';

export const componentsStyles: Theme['components'] = {
  ...themeV2.components,
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        padding: 0,
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[4],
        position: 'relative',
        overflowY: 'unset',
        gap: theme.spacing(2),
        '&.padding-0': {
          padding: 0,
        },
        '&.gap-0': {
          gap: 0,
        },
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme: { typography, spacing } }) => ({
        ...typography.h4,
        padding: spacing(3),
        paddingBottom: 0,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: () => ({ [`&.padding-0`]: { padding: 0 } }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme: { spacing } }) => ({ padding: spacing(3), paddingTop: 0 }),
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme: { palette, spacing, typography } }) => ({
        borderRadius: 0,
        border: 0,
        backgroundColor: palette.common.white,

        '.MuiDataGrid-columnHeaderTitle': {
          ...typography.body1,
          fontWeight: 600,
          whiteSpace: 'normal',
        },
        '.MuiDataGrid-columnHeader': {
          backgroundColor: palette.common.white,
          '&:focus': {
            outline: 'transparent',
          },
        },

        // Scrollbar
        '.MuiDataGrid-scrollbarFiller': {
          backgroundColor: '#ffffff !important',
        },

        // Containers
        '--DataGrid-containerBackground': palette.common.white,

        '.MuiDataGrid-topContainer': {
          backgroundColor: '#ffffff',
          paddingLeft: spacing(2),
        },

        [`&.${dataGridNoHorizontalPaddingClass}`]: {
          '& .MuiDataGrid-topContainer, & .MuiDataGrid-row': {
            paddingLeft: 0,
          },
        },
        [`&.${dataGridHorizontalPaddingXSClass}`]: {
          '& .MuiDataGrid-topContainer, & .MuiDataGrid-row': {
            paddingLeft: spacing(1),
          },
        },

        '.MuiDataGrid-footerContainer': {
          marginTop: 4,
          marginBottom: 2,
          justifyContent: 'center',
        },

        '& .MuiDataGrid-row': {
          ':last-of-type': {
            marginBottom: '2px',
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
        [`&.${dataGridWithCellBordersClass}`]: {
          '.MuiDataGrid-topContainer': {
            backgroundColor: '#ffffff',
            paddingLeft: '0 !important',
            '& .MuiDataGrid-columnHeader': {
              ':nth-of-type(2)': { paddingLeft: spacing(3) },
            },
          },
          '& .MuiDataGrid-row': {
            paddingLeft: 0,
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            border: `1px solid ${palette.divider}`,
            flexGrow: 1,
            borderLeft: 'none',
            borderTop: 'none',
            ':last-of-type': { borderRight: 0 },
            ':nth-of-type(2)': { paddingLeft: spacing(3) },
          },
        },

        // Cell
        '.MuiDataGrid-cell': {
          ...typography.body2,
          display: 'flex',
          alignItems: 'center',
          alignContent: 'center',
          color: palette.action.active,
          fontWeight: 400,
          whiteSpace: 'normal !important',

          [`&.${dataGridNoHorizontalPaddingClass}`]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
          [`&.${dataGridBoldClass}`]: {
            overflow: 'visible',
            fontWeight: 500,
            color: palette.text.primary,
          },
          [`&.${dataGridCellNoFocusOutlineClass}:focus`]: {
            outline: 'none',
          },
          ':focus-within, :focus': {
            outline: 'transparent',
          },
        },

        '.MuiDataGrid-cell--pinnedRight, .MuiDataGrid-filler--pinnedRight, .MuiDataGrid-filler': {
          backgroundColor: `${palette.common.white} !important`,
        },
        '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-filler--pinnedLeft': {
          backgroundColor: `${palette.common.white} !important`,
        },
      }),

      row: ({ theme: { palette, spacing, shadows } }) => ({
        paddingLeft: spacing(2),

        [`&.${dataGridColorRowClass}`]: {
          backgroundColor: `${palette.grey.A100} !important`,
        },
        [`&.${dataGridWhiteRowClass}`]: {
          backgroundColor: `${palette.common.white} !important`,
        },

        [`&.${dataGridRowHoverClass}`]: {
          '&:hover': {
            background: `${palette.background.secondary} !important`,
            boxShadow: shadows[2],
            zIndex: 2,
          },
        },
        [`&.${dataGridSelectedRowClass}`]: {
          backgroundColor: `${palette.background.secondary} !important`,
          boxShadow: shadows[2],
          zIndex: 2,
        },
      }),

      cell: () => ({
        [`&.${dataGridCellHoverClass}`]: {
          '&:hover': {
            cursor: 'pointer',
          },
        },
      }),
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        fontFamily: 'inherit',
      },
    },
    defaultProps: {
      disableRipple: true,
      disableTouchRipple: true,
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({ color: theme.palette.text.secondary }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderWidth: ownerState.size === 'large' ? 2 : 1.5,
        borderStyle: 'solid',
        borderRadius: Number(theme.shape.borderRadius) * 2,
        textTransform: 'none',
        strokeWidth: 2,
        fontWeight: 500,

        '&:hover': {
          borderWidth: ownerState.size === 'large' ? 2 : 1.5,
          borderStyle: 'solid',
          boxShadow: theme.shadows[1],
          transition: 'none', // Remove for smoother border transition
          borderColor: theme.palette.grey[900],
          color: theme.palette.common.white,
          background: theme.palette.grey[900],
        },
        '&:focus-visible': {
          boxShadow: theme.shadows[1],
          outline: `1px solid ${theme.palette.grey[900]}`,
          outlineOffset: 3,
        },
        '&:disabled': {
          borderWidth: ownerState.size === 'large' ? 2 : 1.5,
          borderStyle: 'solid',
          background: theme.palette.grey.A100,
          color: theme.palette.text.disabled,
          borderColor: theme.palette.grey.A100,
        },
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 500,
        padding: '9px 16px',
      }),
      sizeLarge: ({ theme }) => ({
        ...theme.typography.body1,
        fontWeight: 500,
        padding: '10px 38px',
      }),
      containedPrimary: ({ theme }) => ({
        color: theme.palette.common.white,
        background: theme.palette.primary.main,
        transition: 'none', // Remove for smoother border transition
      }),
      containedSecondary: ({ theme }) => ({
        color: theme.palette.text.primary,
        background: theme.palette.gradient.secondary,
        borderColor: 'textSecondary',
        transition: 'none', // Remove for smoother border transition
      }),
      containedWarning: ({ theme }) => ({
        color: theme.palette.text.primary,
        background: theme.palette.warning.main,
        borderColor: theme.palette.warning.main,
      }),
      contained: {
        border: 'none',
        '&.MuiButton-sizeSmall': {
          padding: '10px 20px',
          ':hover, :disabled': {
            border: 0,
            padding: '10px 20px',
          },
        },
        '&.MuiButton-sizeLarge': {
          padding: '12px 40px',
          ':hover, :disabled': {
            border: 0,
            padding: '12px 40px',
          },
        },
        '&.MuiButton-colorError': {
          color: 'white',
        },
      },
      outlined: {
        '&.MuiButton-sizeSmall': {
          padding: '8.5px 18.5px',
          ':hover, :disabled': {
            padding: '8.5px 18.5px',
          },
        },
      },
      outlinedPrimary: ({ theme }) => ({
        color: theme.palette.primary.main,
        background: `linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white}) padding-box, ${theme.palette.primary.main} border-box`,
        transition: 'none', // Remove for smoother border transition,
      }),
      outlinedSecondary: ({ theme }) => ({
        color: theme.palette.text.secondary,
        background: `linear-gradient(${theme.palette.common.white}, ${theme.palette.common.white}) padding-box, ${theme.palette.gradient.secondary} border-box`,
        transition: 'none', // Remove for smoother border transition
      }),
      outlinedWarning: ({ theme }) => ({
        color: theme.palette.text.secondary,
        background: theme.palette.common.white,
        borderColor: theme.palette.warning.main,
      }),
      text: () => ({
        borderColor: 'transparent',
        transition: 'none', // Remove for smoother border transition
      }),
    },
    variants: [
      {
        props: { color: 'grey', variant: 'outlined' },
        style: ({ theme }) => ({
          color: theme.palette.text.secondary,
          background: theme.palette.common.white,
          borderColor: theme.palette.grey.A100,
        }),
      },
      {
        props: { color: 'grey', variant: 'contained' },
        style: ({ theme }) => ({
          color: theme.palette.text.secondary,
          background: theme.palette.common.white,
          borderColor: theme.palette.common.white,
        }),
      },
      {
        props: { variant: 'liquid' as any },
        style: ({ theme }) => {
          const transitionType = `cubic-bezier(0.2, 0.9, 0.3, 1.5)`;
          const transitionDuration = `0.3s`;
          return {
            color: theme.palette.primary.main,
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'transparent',
            borderRadius: `calc(${Number(theme.shape.borderRadius) * 2}px)`,
            boxShadow: `inset 2px 2px 1px 0 rgba(255, 255, 255, 0.3), inset -2px -2px 2px 1px rgba(255, 255, 255, 0.3), 0 4px 8px 0 ${alpha(
              theme.palette.primary.main,
              0.2
            )}, 0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            transition: `transform ${transitionDuration} ${transitionType}, border-radius ${transitionDuration} ${transitionType}, box-shadow ${transitionDuration} ${transitionType}, background ${transitionDuration} ${transitionType}`,
            textTransform: 'none',
            border: 'none',

            '&.MuiButton-sizeSmall': {
              fontSize: theme.typography.body2.fontSize,
              padding: '10px 20px',
            },
            '&.MuiButton-sizeMedium': {
              fontSize: theme.typography.body1.fontSize,
              padding: '12px 28px',
            },
            '&.MuiButton-sizeLarge': {
              fontSize: theme.typography.h6.fontSize,
              padding: '14px 40px',
            },

            '&:hover,&:focus-visible': {
              color: theme.palette.primary.main,
              transform: 'scale(1.05)',
              background: 'rgba(255, 255, 255, 0.08)',
              border: 'inherit',
              boxShadow: `inset 2px 2px 1px 0 rgba(255, 255, 255, 0.35), inset -2px -2px 2px 1px rgba(255, 255, 255, 0.35), 0 6px 14px 0 ${alpha(
                theme.palette.primary.main,
                0.25
              )}, 0 10px 28px 0 ${alpha(theme.palette.primary.main, 0.25)}`,
              outline: 'none',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
              background: 'rgba(255,255,255,0.04)',
              boxShadow: 'none',
            },
          };
        },
      },
    ],
  },

  MuiToggleButton: {
    defaultProps: {
      disableFocusRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        borderWidth: ownerState.size === 'large' ? 2 : 1,
        borderStyle: 'solid',
        borderRadius: Number(theme.shape.borderRadius) * 2,
        textTransform: 'none',
        background: theme.palette.common.white,
        '&.Mui-selected': {
          borderColor: theme.palette.primary.main,
          background: theme.palette.common.white,
        },
        '&:hover, &.Mui-selected:hover': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          boxShadow: theme.shadows[1],
          transition: 'none', // Remove for smoother border transition
          borderColor: theme.palette.grey[900],
          color: theme.palette.common.white,
          background: theme.palette.grey[900],
        },
        '&:focus-visible': {
          boxShadow: theme.shadows[1],
        },
        '&:disabled': {
          borderWidth: ownerState.size === 'large' ? 2 : 1,
          borderStyle: 'solid',
          background: theme.palette.grey.A100,
          color: theme.palette.text.disabled,
          borderColor: theme.palette.grey.A100,
        },
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 500,
        padding: '7.5px',
      }),
      sizeLarge: ({ theme }) => ({
        ...theme.typography.body1,
        fontWeight: 500,
        padding: '10px',
      }),

      selected: ({ theme }) => ({
        borderColor: theme.palette.primary.main,
        background: theme.palette.common.white,
      }),
    },
    // css state classes didnt work for primary, secondary, standard
    variants: [
      {
        props: { color: 'primary' },
        style: ({ theme: { palette } }) => ({
          color: palette.primary.main,
          '&.Mui-selected': {
            borderColor: palette.primary.main,
          },
        }),
      },
      {
        props: { color: 'secondary' },
        style: ({ theme: { palette } }) => ({
          color: palette.secondary.main,
          '&.Mui-selected': {
            borderColor: palette.secondary.main,
          },
        }),
      },
      {
        props: { color: 'warning' },
        style: ({ theme: { palette } }) => ({
          color: palette.warning.main,
          '&.Mui-selected': {
            borderColor: palette.warning.main,
          },
        }),
      },
      {
        props: { color: 'success' },
        style: ({ theme: { palette } }) => ({
          color: palette.success.main,
          '&.Mui-selected': {
            borderColor: palette.success.main,
          },
        }),
      },
    ],
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme: { palette, typography, shape, spacing } }) => ({
        color: palette.text.primary,
        ...typography.body2,
        fontWeight: 600,
        borderRadius: shape.borderRadius,
        '&.MuiButtonBase-root': {
          '& > :first-of-type': {
            outline: `2px solid ${palette.divider}`,
            borderRadius: Number(shape.borderRadius) * 5,
            margin: spacing(1.5),
          },
        },
      }),
      page: ({ theme: { palette } }) => ({
        '&.Mui-selected': {
          background: palette.primary.main,
          color: palette.common.white,
        },
      }),
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: () => ({
        fontWeight: `${400} !important`,
      }),
      shrink: ({ ownerState }) => ({
        ...(ownerState.shrink && {
          fontSize: '16px !important',
        }),
      }),
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: ({ theme: { palette, shape, transitions } }) => ({
        width: 45,
        height: 25,
        padding: 0,

        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 2.5,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            '& + .MuiSwitch-track': {
              backgroundColor: palette.primary.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            color: palette.grey[100],
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
          },
        },
        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          color: palette.common.white,
          width: 20,
          height: 20,
        },
        '& .MuiSwitch-track': {
          borderRadius: Number(shape.borderRadius) * 4,
          backgroundColor: palette.grey[200],
          opacity: 1,
          transition: transitions.create(['background-color'], {
            duration: 500,
          }),
        },
      }),
    },
  },
  MuiMenu: {
    defaultProps: {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
    },
    styleOverrides: {
      root: ({ theme: { palette } }) => ({
        '& .MuiPaper-root': { padding: 0 },
        '& .MuiMenu-list': {
          padding: 0,
          '& .MuiListSubheader-root': {
            paddingTop: 0,
            lineHeight: 3,
            background: palette.grey.A100,
          },
        },
      }),
    },
  },

  MuiMenuItem: {
    defaultProps: {},
    styleOverrides: {
      root: ({ ownerState, theme: { spacing, palette } }) => ({
        gap: spacing(3),
        justifyContent: 'space-between',
        padding: `${spacing(2)} ${spacing(4)}`,
        borderTop: `1px solid ${palette.divider}`,

        '&:last-of-type': {
          borderBottom: `1px solid ${palette.divider}`,
        },

        ':hover': {
          backgroundColor: palette.divider,
        },
        ':focus': {
          backgroundColor: palette.divider,
        },
        ':mouseOver': {
          backgroundColor: palette.divider,
        },
        ...(ownerState.color === 'warning' && {
          backgroundColor: palette.warning.main,
          ':hover': {
            backgroundColor: palette.warning.dark,
          },
          ':focus': {
            backgroundColor: palette.warning.dark,
          },
        }),
        '& .MuiSvgIcon-root': {
          color: palette.primary.main,
        },
      }),
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: ({ ownerState, theme: { typography, palette } }) => ({
        '& .MuiFormLabel-root': {
          ...(ownerState.size === 'medium' ? typography.body1 : typography.body2),
          fontWeight: 500,
        },
        '& .MuiInputBase-input': {
          height: '20px',
        },

        '& .MuiOutlinedInput-root': {
          '&.Mui-focused': {
            background: palette.common.white,
          },
        },
        '& .MuiOutlinedInput-input': {
          '&:-webkit-autofill': {
            WebkitBoxShadow: ' 0 0 0 30px white inset !important;',
          },
        },
      }),
    },
  },

  MuiOutlinedInput: {
    ...themeV2.components?.MuiOutlinedInput,
    variants: [
      {
        props: { size: 'xs' as any },
        style: () => ({
          minWidth: 68,
          height: 32,
          '& input': {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }),
      },
    ],
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme: { spacing } }) => ({
        '& .MuiListSubheader-root': {
          paddingTop: spacing(1),
        },
      }),
    },
  },

  MuiSelect: {
    defaultProps: {
      IconComponent: ExpandMoreRounded,
    },
    styleOverrides: {
      root: () => ({
        '.MuiOutlinedInput-input': {
          padding: '16px 14px', // compensate for 2px border
        },

        '.MuiInputBase-inputSizeSmall': {
          padding: '8.5px 14px', // compensate for 14px font size
          minHeight: '23px',
        },
      }),
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        marginRight: theme.spacing(1),
        '& .MuiTabs-indicator': {
          display: 'none',
        },

        [`&.${TabsClass.underlined}`]: {
          width: '100%',
          minHeight: 0,
          marginRight: 0,
          borderBottom: `1px solid ${theme.palette.grey.A100}`,

          '& .MuiTab-root': {
            minHeight: '10px',
            paddingTop: 0,
            paddingBottom: 12,
            borderRadius: 0,
          },

          '& .MuiTabs-flexContainer': {
            justifyContent: 'start',
            gap: theme.spacing(3),
          },

          '& .MuiTabs-indicator': {
            display: 'block',
            background: theme.palette.primary.main,
            height: '4px',
            borderRadius: Number(theme.shape.borderRadius) * 5,
          },

          '& .MuiTabScrollButton-horizontal': {
            height: 20,
            width: 20,
          },
        },
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${theme.palette.grey.A100}`,
        borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
        borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
        textTransform: 'none',
        fontSize: theme.typography.body1.fontSize,
        color: theme.palette.text.secondary,
        padding: theme.spacing(1, 6),
        minHeight: theme.spacing(6),

        [`&.tab--small`]: { padding: theme.spacing(1, 2) },

        [`:not(.${TabsClass.underlined}):not(.${blockTab})`]: {
          backgroundColor: theme.palette.grey[50],
        },
        [`&:hover:is(.${blockTab})`]: {
          backgroundColor: theme.palette.grey.A100,
        },

        [`&.Mui-selected:not(.${TabsClass.underlined}):not(.${blockTab})`]: {
          boxShadow: '6px 8px 8px rgba(15, 15, 15, 0.2)',
          backgroundColor: theme.palette.common.white,
          color: theme.palette.text.secondary,
          borderBottom: 0,
          fontWeight: 600,
        },
        [`&.Mui-disabled:not(.${blockTab})`]: {
          borderRadius: 0,
          border: 0,
        },

        [`&.${TabsClass.underlined}`]: {
          border: 0,
          padding: 0,
          spacing: theme.spacing(4),

          '&.Mui-selected': {
            color: theme.palette.text.primary,
            fontWeight: 700,
          },
        },
        [`&.${blockTab}`]: {
          padding: theme.spacing(2),
          borderRadius: 0,
          border: 0,

          '&.Mui-selected': {
            backgroundColor: '#E4E4E4',
            color: theme.palette.text.primary,
          },
        },
      }),
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&::before': { height: 0 },
        '&.active': {
          '> .MuiAccordionSummary-root': {
            color: theme.palette.primary.main,
          },
        },
      }),
    },
    defaultProps: {
      disableGutters: true,
      TransitionProps: { unmountOnExit: true, mountOnEnter: true },
      elevation: 0,
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme: { shape, spacing, palette } }) => ({
        padding: spacing(0.5),
        backgroundColor: palette.primary.A200,
        borderRadius: Number(shape.borderRadius) * 2,
        gap: spacing(0.5),
        [`&.${ToggleButtonGroupClass.outline} .MuiToggleButtonGroup-grouped`]: {
          '&.Mui-selected, &.Mui-selected:hover, &:hover, &:focus': {
            color: 'inherit',
            background: 'transparent',
            boxShadow: 'none',
            outline: `3px solid ${palette.primary.main}`,
            outlineOffset: '-3px',
          },
          '&:hover, &.Mui-selected:hover': {
            outlineColor: palette.grey[900],
          },
          '&.Mui-selected.MuiToggleButton-primary, &.Mui-selected.MuiToggleButton-standard': {
            color: 'inherit',
            background: 'transparent',
          },
        },
      }),
      grouped: ({ theme: { spacing, palette, shape, shadows } }) => ({
        color: palette.text.secondary,
        background: 'transparent',
        border: 'none !important',
        fontWeight: 600,
        '&.MuiToggleButtonGroup-grouped': {
          borderRadius: shape.borderRadius,
          '&:hover,:focus': {
            boxShadow: shadows[1],
            color: palette.common.white,
            background: palette.grey[900],
          },
        },
        '&.Mui-disabled': {
          color: palette.grey[200],
        },
        '&.Mui-selected': {
          '&.MuiToggleButton-primary, &.MuiToggleButton-standard': {
            color: palette.common.white,
            background: palette.primary.main,
          },
          '&:hover': {
            boxShadow: shadows[1],
            color: palette.common.white,
            background: palette.grey[900],
          },
        },
        '&.MuiToggleButton-sizeSmall': {
          padding: `${spacing(0.5)} ${spacing(1.5)}`,
          whiteSpace: 'nowrap',
        },
        '&.MuiToggleButton-sizeMedium': {
          padding: `${spacing(1)} ${spacing(1.5)}`,
        },
      }),
      middleButton: ({ theme: { palette, spacing } }) => ({
        '&::after': {
          content: '""',
          display: 'block',
          width: '0.5px',
          backgroundColor: palette.grey[100],
          position: 'absolute',
          height: '24px',
          right: `-${spacing(0.5)}`,
          top: '50%',
          transform: 'translateX(-50%) translateY(-50%)',
        },
      }),
      firstButton: ({ theme: { palette, spacing } }) => ({
        '&::after': {
          content: '""',
          display: 'block',
          width: '0.5px',
          backgroundColor: palette.grey[100],
          position: 'absolute',
          height: '24px',
          right: `-${spacing(0.5)}`,
          top: '50%',
          transform: 'translateX(-50%) translateY(-50%)',
        },
      }),
    },
  },
  MuiStepper: {
    styleOverrides: {
      horizontal: ({ theme: { spacing, palette, typography, shape } }) => ({
        justifyContent: 'space-between',
        '.MuiStep-root': {
          padding: spacing(0, 2.25),
          flex: 'none',

          ':first-of-type': {
            paddingLeft: 0,
          },

          ':last-of-type': {
            paddingRight: 0,
          },
        },

        '.MuiStepConnector-root': {
          position: 'inherit',
          flex: 1,
          alignSelf: 'center',
          '&.Mui-active, &.Mui-completed': {
            '.MuiStepConnector-line': {
              borderColor: palette.primary.main,
            },
          },
          '.MuiStepConnector-line': {
            borderTopWidth: '2px',
            borderColor: palette.grey[400],
            minWidth: '10px',
          },
        },

        '.MuiStepLabel-root': {
          padding: spacing(1, 2),
          overflow: 'hidden',
          border: `2px solid transparent`,
          borderRadius: Number(shape.borderRadius) * 3,
          backgroundColor: alpha(palette.grey.A100, 0.3),

          ' svg': {
            width: '16px',
            height: '16px',
            fill: palette.grey[400],
          },

          '.MuiStepLabel-label': {
            color: palette.grey[400],
            ...typography.body2,
            fontWeight: 700,
          },

          '&.Mui-active, &.Mui-completed': {
            ' svg': {
              fill: palette.primary.main,
            },
            borderColor: palette.primary.main,
            '.MuiStepLabel-label': {
              color: palette.primary.main,
            },
          },

          '&.Mui-completed': {
            backgroundColor: alpha(palette.grey.A100, 0.3),
            border: `2px solid transparent`,
          },

          '&.Mui-active': {
            background: palette.common.white,
          },
        },
      }),
    },
  },
};
